import { CheckCircle2, XCircle } from "lucide-react";
import type { Universe } from "./deskData";

/**
 * Today's coins and the coins left out, each with a one-line reason
 * (backend services/dailyUniverse.ts). Beginner copy first, numbers second.
 */
export function UniverseInfo({ universe }: { universe: Universe }) {
	return (
		<div className="space-y-5 text-sm">
			<p className="text-muted">
				Day trading needs coins that are busy and moving <em>today</em>, so the list is picked fresh every day at
				00:00 UTC. The Telegram scanner and this desk watch the same list.
				{universe.fallback && " Market data was unavailable, so this is the last known list."}
			</p>

			<section>
				<h3 className="mb-1 font-semibold">How a coin gets picked</h3>
				<ol className="list-decimal space-y-1 pl-5 text-muted">
					<li><span className="text-foreground">Busy:</span> at least $150M traded in the last 24 hours, so you get filled near the price you see.</li>
					<li><span className="text-foreground">Moving enough:</span> its normal 1-hour move is at least 0.6%. Smaller moves can't pay for trading fees.</li>
					<li><span className="text-foreground">Proven:</span> trading for more than a year, so the strategy can be tested on it.</li>
					<li><span className="text-foreground">Not a pump:</span> huge volume while ignoring Bitcoin usually means a pump. We skip those.</li>
				</ol>
			</section>

			<section>
				<h3 className="mb-2 font-semibold">Today's {universe.included.length} coins</h3>
				<ul className="space-y-1.5">
					{universe.included.map(c => (
						<li key={c.symbol} className="flex items-start gap-2">
							<CheckCircle2 className="mt-0.5 size-4 shrink-0 text-profit" aria-hidden />
							<div className="min-w-0 flex-1">
								<div className="flex flex-wrap items-baseline gap-x-2">
									<span className="font-medium">{c.symbol.replace("USDT", "")}</span>
									<span className="text-xs text-muted">{c.why}</span>
								</div>
								{c.volumeM > 0 && (
									<div className="num text-2xs text-muted">
										${c.volumeM.toLocaleString()}M traded · moves ~{c.atrPct}% an hour · follows BTC {Math.round(c.btcCorr * 100)}%
									</div>
								)}
							</div>
						</li>
					))}
				</ul>
			</section>

			{universe.excluded.length > 0 && (
				<section>
					<h3 className="mb-2 font-semibold">Left out today</h3>
					<ul className="space-y-1.5">
						{universe.excluded.map(c => (
							<li key={c.symbol} className="flex items-start gap-2">
								<XCircle className="mt-0.5 size-4 shrink-0 text-muted" aria-hidden />
								<span><span className="font-medium">{c.symbol.replace("USDT", "")}</span> <span className="text-muted">· {c.reason}</span></span>
							</li>
						))}
					</ul>
				</section>
			)}

			<p className="text-xs text-muted">
				A coin that isn't here today didn't pass these rules. It comes back on the day it does, so there's no watchlist to keep.
			</p>
		</div>
	);
}
